// Shared AI-match score presentation. Mirrors ems/src/helpers/match-score-tiers.js — keep the two in
// lock-step so the participant app and the sponsor portal never label the same match differently.
//
// Two things this fixes:
//
// 1. NO x100 SCALING. Every score (headline and per-criterion) is stored 0-100. The old
//    `val > 1 ? val : val * 100` guard silently multiplied any genuine low score by 100 — a
//    three-rung ladder miss scores ~1.1, which rendered as 110%.
//
// 2. ONE SET OF THRESHOLDS. This app hardcoded `>= 80` for "hot" while the sponsor portal used 70/40
//    in its table and 75/50 in its lead-detail modal.
//
// The thresholds are absolute, which is only meaningful because the 40-95 display curve was removed
// from the engine — scores are genuine 0-100 with a true zero.

export const MATCH_TIER_HIGH = 75;
export const MATCH_TIER_MID = 50;

/** Clamp + round a stored 0-100 score for display. Never scales. */
export function displayScore(value) {
  const num = Number(value);
  if (!Number.isFinite(num)) return 0;
  return Math.round(Math.min(100, Math.max(0, num)));
}

/** @returns {'high'|'mid'|'low'} */
export function matchTierOf(value) {
  const pct = displayScore(value);
  if (pct >= MATCH_TIER_HIGH) return 'high';
  if (pct >= MATCH_TIER_MID) return 'mid';
  return 'low';
}

/** True when the score clears the top tier — the "hot match" bar. */
export function isStrongMatch(value) {
  return matchTierOf(value) === 'high';
}

/**
 * Did this criterion contribute to the headline score?
 *
 * Mirrors the engine, which excludes a 0-weight criterion from both numerator and denominator. A
 * criterion ABSENT from the map still counted (the engine defaults a missing weight to 1.0), so
 * absence must never be read as zero.
 *
 * @param {Object|null} weights criterionName -> weight, from the match's campaign snapshot
 * @param {string} criterionName
 */
export function isCriterionNotCounted(weights, criterionName) {
  if (!weights || typeof weights !== 'object') return false;
  const weight = weights[criterionName];
  if (typeof weight !== 'number') return false;
  return weight <= 0;
}
